import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { PortalPage } from "@/components/portal/PortalPage";
import {
  buildSeo,
  hasFilterQuery,
  humanizeSlug,
  PORTAL_OPERACIONES,
  portalBreadcrumbJsonLd,
  portalDescriptionFor,
  portalItemListJsonLd,
  portalTitleFor,
  type PortalSeoLabels,
} from "@/lib/seo";
import { getPortalSeoData, resolverAliasPortal } from "@/lib/portal-seo.functions";
import { puedeTenerAlias, searchStrings } from "@/lib/portal-alias";

function splitSegs(splat: string | undefined): string[] {
  return (splat ?? "").split("/").filter(Boolean).map((s) => s.toLowerCase());
}

export const Route = createFileRoute("/portal/$")({
  // Reject unknown operation slugs at the very top — real 404.
  // Numeric-only first segments (e.g. legacy /portal/10616) also 404: we
  // do not have a mapping from legacy numeric ids to current listings, so
  // per spec we skip the redirect and just render NotFound.
  beforeLoad: ({ params }) => {
    const first = splitSegs(params._splat)[0];
    if (first && !PORTAL_OPERACIONES.has(first)) throw notFound();
  },
  // Search-derived deps: only the presence of filter query params changes the
  // canonical/robots verdict. We do NOT depend on individual filter values.
  loaderDeps: ({ search }) => ({ filtered: hasFilterQuery(search as any) }),
  loader: async ({ params, deps, location }) => {
    const segments = splitSegs(params._splat);
    // Slugs de ubicaciones fusionadas (alias) → 301 a la URL principal. Se
    // consulta en paralelo con los datos SEO para no añadir espera.
    const search = searchStrings(location.search);
    const aliasP = puedeTenerAlias(segments, search)
      ? resolverAliasPortal({ data: { basePath: "/portal", segments, search } }).catch(() => null)
      : Promise.resolve(null);
    const [seoRes, alias] = await Promise.all([
      getPortalSeoData({ data: { segments } }).then(
        (v) => ({ ok: true as const, v }),
        () => ({ ok: false as const }),
      ),
      aliasP,
    ]);
    if (alias) {
      throw redirect({
        to: alias.pathname as never,
        search: alias.search as never,
        statusCode: 301,
      });
    }
    let seo;
    if (seoRes.ok) {
      seo = seoRes.v;
    } else {
      // If the SEO resolver itself fails (DB hiccup), fall back to a
      // permissive render for /portal and top-level operation pages, but
      // still 404 any deeper unknown facet — we cannot validate it.
      if (segments.length > 1) throw notFound();
      seo = {
        op: null,
        tipo: null,
        provincia: null,
        ciudad: null,
        distrito: null,
        total: 0,
        items: [] as Array<{ id: string; title: string }>,
      };
    }

    // Validate every provided facet segment against the DB. Any unknown
    // slug (tipo/provincia/ciudad/distrito) → real 404 so we stop serving
    // soft-200 portal pages for garbage paths like /portal/traspaso/xyz.
    const checks: Array<[string | undefined, unknown]> = [
      [segments[1], seo.tipo],
      [segments[2], seo.provincia],
      [segments[3], seo.ciudad],
      [segments[4], seo.distrito],
    ];
    for (const [slug, entity] of checks) {
      if (slug && slug !== "todas" && !entity) throw notFound();
    }

    return { segments, seo, filtered: !!deps.filtered };
  },
  head: ({ loaderData, params }) => {
    const segs = splitSegs(params._splat);

    // Loader threw (notFound or error): render a noindex head so the 404
    // body served in place of the portal is never accidentally indexed.
    if (!loaderData) {
      return buildSeo({
        title: "Página no encontrada · Bizaclia",
        description:
          "La página que buscas no existe o ha sido movida. Vuelve al inicio o explora el portal de locales, oficinas y traspasos.",
        path: "/404",
        noindex: true,
      });
    }

    const labels: PortalSeoLabels = {
      op: (loaderData.seo.op?.slug as string | undefined) ?? segs[0] ?? "",
      tipoSlug: segs[1] ?? "",
      tipoNombre: loaderData.seo.tipo?.nombre ?? null,
      provincia:
        loaderData.seo.provincia?.nombre ?? humanizeSlug(segs[2] ?? "") ?? null,
      ciudad:
        loaderData.seo.ciudad?.nombre ?? humanizeSlug(segs[3] ?? "") ?? null,
      distrito:
        loaderData.seo.distrito?.nombre ?? humanizeSlug(segs[4] ?? "") ?? null,
    };

    const total = loaderData.seo.total ?? 0;
    const { title } = portalTitleFor(labels, segs.length);
    const description = portalDescriptionFor(labels, segs.length, total);

    const path = segs.length ? `/portal/${segs.join("/")}` : "/portal";
    const isFiltered = !!loaderData.filtered;

    const jsonLd: Array<Record<string, unknown>> = [
      portalBreadcrumbJsonLd(labels, path),
    ];
    if ((loaderData.seo.items?.length ?? 0) > 0) {
      jsonLd.push(portalItemListJsonLd(loaderData.seo.items));
    }

    return buildSeo({
      title: title.length > 70 ? title.slice(0, 67) + "…" : title,
      description:
        description.length > 160 ? description.slice(0, 157) + "…" : description,
      path,
      noindex: isFiltered,
      jsonLd,
    });
  },
  component: PortalSplat,
  notFoundComponent: PortalNotFound,
});


function PortalSplat() {
  const { segments, seo } = Route.useLoaderData();
  return (
    <PortalPage
      segments={segments}
      seoLabels={{
        op: (seo.op?.slug as string | undefined) ?? segments[0] ?? "",
        tipoSlug: segments[1] ?? "",
        tipoNombre: seo.tipo?.nombre ?? null,
        provincia: seo.provincia?.nombre ?? null,
        ciudad: seo.ciudad?.nombre ?? null,
        distrito: seo.distrito?.nombre ?? null,
      }}
    />
  );
}

function PortalNotFound() {
  return (
    <main className="flex min-h-[70vh] items-center justify-center bg-background px-4 py-16">
      <div className="max-w-lg text-center">
        <p className="text-sm font-semibold text-primary">404</p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Página no encontrada
        </h1>
        <p className="mt-4 text-muted-foreground">
          La ruta del portal que buscas no existe. Prueba con una operación
          válida (traspaso, alquiler o venta) o vuelve al inicio.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ir al inicio
          </Link>
          <Link
            to="/portal/$"
            params={{ _splat: "traspaso" }}
            className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Traspasos
          </Link>
          <Link
            to="/portal/$"
            params={{ _splat: "alquiler" }}
            className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Alquiler
          </Link>
          <Link
            to="/portal/$"
            params={{ _splat: "venta" }}
            className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Venta
          </Link>
        </div>
      </div>
    </main>
  );
}
